/**
 * Custom hook for Razorpay payment integration
 */
import { useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useRazorpayPayment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load Razorpay script dynamically
  const loadRazorpayScript = useCallback(() => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }, []);

  // Create order and open payment
  const initiatePayment = useCallback(async ({
    amount,
    currency = 'INR',
    receipt,
    notes = {},
    schoolId,
    paymentType = 'subscription',
    prefill = {},
    onSuccess,
    onFailure,
    theme = { color: '#f97316' }
  }) => {
    setLoading(true);
    setError(null);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create order on backend
      const response = await fetch(`${API_URL}/api/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: amount * 100, // Convert to paise
          currency,
          receipt: receipt || `rcpt_${Date.now()}`,
          notes,
          school_id: schoolId,
          payment_type: paymentType
        })
      });

      const orderData = await response.json();

      if (!orderData.success) {
        throw new Error(orderData.detail || 'Failed to create order');
      }

      // Handle mock order (when Razorpay is not configured)
      if (orderData.is_mock) {
        // Simulate payment for demo
        const mockPaymentId = `pay_mock_${Date.now()}`;
        const mockSignature = 'mock_signature';
        
        // Verify mock payment
        const verifyResponse = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            razorpay_order_id: orderData.order.id,
            razorpay_payment_id: mockPaymentId,
            razorpay_signature: mockSignature,
            school_id: schoolId
          })
        });

        const verifyData = await verifyResponse.json();
        
        if (verifyData.success) {
          onSuccess?.({
            orderId: orderData.order.id,
            paymentId: mockPaymentId,
            isMock: true
          });
        } else {
          throw new Error('Mock payment verification failed');
        }
        
        setLoading(false);
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'शिक्षा मित्र',
        description: notes.description || 'Payment',
        image: 'https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg',
        order_id: orderData.order.id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            const verifyResponse = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                school_id: schoolId
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              onSuccess?.({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature
              });
            } else {
              throw new Error(verifyData.detail || 'Payment verification failed');
            }
          } catch (err) {
            setError(err.message);
            onFailure?.(err);
          }
        },
        prefill: {
          name: prefill.name || '',
          email: prefill.email || '',
          contact: prefill.contact || ''
        },
        notes: notes,
        theme: theme,
        modal: {
          ondismiss: () => {
            setLoading(false);
            onFailure?.({ message: 'Payment cancelled' });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setError(response.error.description);
        onFailure?.(response.error);
      });
      
      razorpay.open();
      setLoading(false);

    } catch (err) {
      setError(err.message);
      setLoading(false);
      onFailure?.(err);
    }
  }, [loadRazorpayScript]);

  // Wallet recharge specific function
  const rechargeWallet = useCallback(async ({
    schoolId,
    amount,
    userName,
    userPhone,
    userEmail,
    onSuccess,
    onFailure
  }) => {
    await initiatePayment({
      amount,
      receipt: `wallet_${schoolId}_${Date.now()}`,
      notes: {
        school_id: schoolId,
        type: 'wallet_recharge',
        description: `Wallet Recharge - ₹${amount}`
      },
      schoolId,
      paymentType: 'wallet_recharge',
      prefill: {
        name: userName,
        email: userEmail,
        contact: userPhone
      },
      onSuccess,
      onFailure,
      theme: { color: '#10b981' } // Green for wallet
    });
  }, [initiatePayment]);

  // Subscription payment function
  const paySubscription = useCallback(async ({
    schoolId,
    planId,
    planDuration,
    studentCountSlab,
    studentCount,
    schoolName,
    contactName,
    contactPhone,
    contactEmail,
    chatSessionId,
    source,
    onSuccess,
    onFailure
  }) => {
    setLoading(true);
    setError(null);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create subscription order
      const response = await fetch(`${API_URL}/api/razorpay/create-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_id: schoolId,
          plan_id: planId || 'standard',
          plan_duration: planDuration || 'yearly',
          student_count_slab: studentCountSlab || 'small',
          student_count: studentCount,
          school_name: schoolName,
          contact_name: contactName,
          contact_phone: contactPhone,
          contact_email: contactEmail,
          chat_session_id: chatSessionId,
          source: source || 'subscription_modal'
        })
      });

      const orderData = await response.json();

      if (!orderData.success) {
        throw new Error(orderData.detail || 'Failed to create subscription order');
      }

      // Handle mock order
      if (orderData.is_mock) {
        const mockPaymentId = `pay_mock_${Date.now()}`;
        
        const verifyResponse = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            razorpay_order_id: orderData.order.id,
            razorpay_payment_id: mockPaymentId,
            razorpay_signature: 'mock_signature',
            school_id: schoolId
          })
        });

        const verifyData = await verifyResponse.json();
        
        if (verifyData.success) {
          onSuccess?.({
            orderId: orderData.order.id,
            paymentId: mockPaymentId,
            isMock: true,
            planId,
            planDuration,
            autoProvision: verifyData.auto_provision || null
          });
        } else {
          throw new Error('Mock payment verification failed');
        }
        
        setLoading(false);
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'शिक्षा मित्र',
        description: `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan - ${planDuration}`,
        image: 'https://customer-assets.emergentagent.com/job_b71b88a0-de31-4e82-b047-c99df2477168/artifacts/7dlw52ts_logo%20%281%29.jpg',
        order_id: orderData.order.id,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                school_id: schoolId
              })
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              onSuccess?.({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                planId,
                planDuration,
                autoProvision: verifyData.auto_provision || null
              });
            } else {
              throw new Error(verifyData.detail || 'Payment verification failed');
            }
          } catch (err) {
            setError(err.message);
            onFailure?.(err);
          }
        },
        prefill: {
          name: contactName,
          email: contactEmail || '',
          contact: contactPhone
        },
        notes: {
          school_name: schoolName,
          plan_id: planId,
          plan_duration: planDuration
        },
        theme: { color: '#f97316' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            onFailure?.({ message: 'Payment cancelled' });
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setError(response.error.description);
        onFailure?.(response.error);
      });
      
      razorpay.open();
      setLoading(false);

    } catch (err) {
      setError(err.message);
      setLoading(false);
      onFailure?.(err);
    }
  }, [loadRazorpayScript]);

  return {
    loading,
    error,
    initiatePayment,
    rechargeWallet,
    paySubscription
  };
}

export default useRazorpayPayment;
